/* global document */
import { CursorPosition } from '@engoo/realtime-protocol/selection';
import { getFlagLabelTextColor } from 'app/components/call/SharedSelection/colors';
import {
  findNodeAtOffset,
  leftWhiteSpaceLength,
  rightWhiteSpaceLength,
  textLength,
} from 'app/components/call/SharedSelection/dom';
import { INodePoint } from 'app/components/call/SharedSelection/LazySortedNodes';
import { IColorRect, IRect, mergeLines, splitOverlappingRects } from 'app/components/call/SharedSelection/rects';
import { useStickyHeight } from 'app/components/layout/Page/StickyContext';
import GetScroll, { IScrollEvent } from 'app/components/scroll/GetScroll';
import { IBounds, ScrollCtrlConsumer } from 'app/components/scroll/ScrollContext';
import { usePeers } from 'app/containers/rtc/PeersContext';
import { KeyPath } from 'app/models/entityStateSync/keypath';
import { KeyPathRootState } from 'app/models/entityStateSync/types';
import { Selection, ParticipantSelections } from 'app/models/rtc/selection/constants';
import { notify } from 'app/services/errorReporter';
import { createStyles, ExtractStylesClassGetter, IStyles, keyframes } from 'app/utils/css';
import { flattenIterators } from 'app/utils/mergeIterables';
import { Map } from 'immutable';
import React, { Fragment, FunctionComponent, PureComponent } from 'react';
import { createSelector } from 'reselect';
import throttle from 'lodash/throttle';
import flatten from 'lodash/flatten';
import uniq from 'lodash/uniq';
import isEqual from 'lodash/isEqual';
import RBush, { BBox } from 'rbush';
import { CallbackRef } from 'app/utils/react';
import useMeasure, { ISize } from 'app/components/layout/useMeasure';
import { useEntityStateSyncContext } from 'app/containers/rtc/EntityStateSyncContext';
import { IPresenceContext, usePresenceContext } from 'app/containers/rtc/PresenceContext';

export const SCROLL_THROTTLE = 100;
const SELECTION_FLAG_FADEOUT_DELAY_SECONDS = 0.4;
const SELECTION_FLAG_FADEOUT_DURATION_SECONDS = 0.5;

export const FLAG_POLE_WIDTH = 4;

interface IProps {
  readonly nodes: Map<KeyPath, INodePoint>;
  readonly selections: ParticipantSelections;
}

interface IDeps {
  readonly scrollViewport?: IScrollEvent;
  readonly scrollTo: (position: { y?: number }) => void;
  readonly stickyContainerHeight: number;
  readonly size: ISize;
  readonly css: ExtractStylesClassGetter<typeof Styles>;
  readonly getParticipantName: IPresenceContext['getParticipantName'];
  readonly keyPathRootState?: KeyPathRootState;
  readonly measureRef: CallbackRef;
}

/**
 * The edge where the flag for off-screen selection should be
 * attached to
 */
enum FixedType {
  Top = 'top',
  Bottom = 'bottom',
}

interface IFlagPosition {
  readonly x: number;
  readonly y: number;
  readonly height: number;
  readonly fixed?: FixedType;
  readonly targetScrollTop?: number;
}

/**
 * Represents a cursor marker with user name that shows up either
 * on the beginning or the end of the selection.
 */
interface IFlag extends IFlagPosition {
  readonly label: string;
  readonly color: string;
  readonly textColor: string;
  readonly position: CursorPosition;
}

interface IState {
  forceDisplayFlagLabel: string[];
}

interface IRTreeRect extends BBox {
  colors: readonly string[];
}

export class SharedSelectionRender extends PureComponent<IProps & IDeps, IState> {
  // Ref to the containing object.
  private node: HTMLDivElement | null = null;

  /**
   * Only for invalidation. We need to invalidate based on size
   * when the viewport resizes and for changing entities state,
   * because they affect the layout of the document. If we don't
   * invalidate, the selection will get out of sync with the content
   * it overlays over
   */
  private readonly dirtyChecks = createSelector(
    ({ size }: IDeps & IProps) => size,
    ({ keyPathRootState }: IDeps & IProps) => keyPathRootState,
    // Return a new blank object when one of the above changes
    () => ({}),
  );

  private readonly getSelections = createSelector(
    ({ selections }: IDeps & IProps) => selections,
    (selections) => selections.valueSeq(),
  );

  private readonly getSelectionLines = createSelector(
    this.dirtyChecks,
    ({ nodes }: IDeps & IProps) => nodes,
    this.getSelections,
    (dirtyChecks, ...args) => {
      const surface = this.node && this.node.getBoundingClientRect();
      return surface ? getSelectionLines(surface, ...args) : [];
    },
  );

  private readonly getSelectionFlags = createSelector(
    this.dirtyChecks,
    ({ scrollViewport }: IDeps & IProps) => scrollViewport,
    ({ stickyContainerHeight }) => stickyContainerHeight,
    ({ nodes }: IDeps & IProps) => nodes,
    this.getSelections,
    ({ getParticipantName }: IDeps & IProps) => getParticipantName,
    (dirtyChecks, ...args) => {
      const surface = this.node && this.node.getBoundingClientRect();
      return surface ? getSelectionFlags(surface, ...args) : [];
    },
  );

  /*
   * All attempts to enable pointer-events for shared selection were deemed too unreliable.
   * The problem with native events seems to be that we are re-rendering a moving dom node inside the selection
   * which is throwing that selection off (it starts jumping, flickering and selecting all content on the page).
   * So instead we are tracking selection rectangles on our own in an R-Tree.
   * This way we can leave pointer-events disabled and track hover using mouseover/touchstart events
   * and querying the tree
   */
  private selectionRectsTree = new RBush<IRTreeRect>();

  private toRtreeItem = (selection: IColorRect): IRTreeRect => ({
    colors: selection.colors,
    minX: selection.left,
    minY: selection.top,
    maxX: selection.left + selection.width,
    maxY: selection.top + selection.height,
  });

  private reindexSelectionRects = (selections: IColorRect[]) => {
    this.selectionRectsTree.clear();
    this.selectionRectsTree.load(selections.map(this.toRtreeItem));
  };

  private getTouchCoordinates = (touches: React.TouchList) => {
    const { clientX, clientY } = touches[0];

    return { x: clientX, y: clientY };
  };

  private highlightSelectionNameFlag = throttle((x: number, y: number) => {
    if (!this.node) return;
    const { left: wrapLeft, top: wrapTop } = this.node.getBoundingClientRect();

    const rects = this.selectionRectsTree.search({
      minX: x - wrapLeft,
      maxX: x - wrapLeft,
      minY: y - wrapTop,
      maxY: y - wrapTop,
    });

    const colors = uniq(flatten(rects.map(rect => rect.colors)));
    if (isEqual(colors, this.state.forceDisplayFlagLabel)) return;

    this.setState(() => ({ forceDisplayFlagLabel: colors }));
  }, 1000 / 30); // 30fps

  private stopHighlightingNameFlagAfterTap = (e: React.TouchEvent) => {
    if (this.state.forceDisplayFlagLabel.length === 0) return;

    // Without this browser will fire an additional click event
    // and the label will never fade out
    e.preventDefault();

    this.setState(() => ({ forceDisplayFlagLabel: [] }));
  };

  private callbackRef = (node: HTMLDivElement | null) => {
    this.node = node;
    this.props.measureRef(node);
  };

  public state: IState = { forceDisplayFlagLabel: [] };

  public componentDidUpdate(prevProps: IProps & IDeps) {
    const prevLines = this.getSelectionLines(prevProps);
    const lines = this.getSelectionLines(this.props);

    const selectionChanged = prevLines !== lines;
    if (selectionChanged) {
      this.reindexSelectionRects(lines);
    }
  }

  public render() {
    const { css, children, scrollTo } = this.props;
    const lines = this.getSelectionLines(this.props);
    const flags = this.getSelectionFlags(this.props);

    return (
      <div
        className={css('wrap')}
        ref={this.callbackRef}
        data-testid="render-wrapper"
        onMouseMove={({ clientX, clientY }) => this.highlightSelectionNameFlag(clientX, clientY)}
        onTouchStart={({ touches }) => {
          // If somehow the touch event has no touches -- ignore it
          // If it's a multi-touch event -- ignore it, user probably wanted to do something else
          if (touches.length !== 1) return;

          const { x, y } = this.getTouchCoordinates(touches);
          this.highlightSelectionNameFlag(x, y);
        }}
        onTouchEnd={this.stopHighlightingNameFlagAfterTap}
      >
        <div className={css('resetStackingContext')}>
          {children}
        </div>

        {lines.map(({ top, left, width, height, colors }, i) =>
          <span
            data-testid={`highlightSpan-${i}`}
            key={`block-${i}`}
            className={css('block')}
            style={{
              top: `${top}px`,
              left: `${left}px`,
              width: `${width}px`,
              height: `${height}px`,
              background: getBackground(colors),
            }}
          />,
        )}

        {flags.map(({ fixed, x, y, height, position, label, color, textColor, targetScrollTop }, i) =>
          <Fragment key={`flag-${i}`}>
            {fixed && <div
              className={css('fixedFlag', { fixed, position })}
              style={{
                top: `${y}px`,
                left: `${x}px`,
              }}
            >
              <div
                className={css('fixedFlagArrow', { fixed })}
                style={{ background: color }} data-testid="flag-arrow"
              />

              <div
                className={css('fixedFlagText')}
                style={{
                  color: textColor,
                  background: color,
                }}
                // Use mouse down / up events with `preventDefault` to
                // prevent clicking on the label changing the selection
                onMouseDown={(event) => event.preventDefault()}
                onMouseUp={() => scrollTo({ y: targetScrollTop })}
                data-testid="flag-text"
              >
                {label}
              </div>
            </div>}

            {!fixed && (
              <div
                className={css('flagLabel', {
                  position,
                  forceDisplay: this.state.forceDisplayFlagLabel.includes(color),
                })}
                // We need to invalidate the dom element whenever selection changes to
                // cause the animation to rerun, revealing the label again, hence x-y compound key
                key={`${x}_${y}`}
                style={{
                  top: `${y}px`,
                  left: `${x}px`,
                  color: textColor,
                  background: color,
                }}
                data-testid="flag-label"
              >
                {label}
              </div>
            )}
            {!fixed && <div
              className={css('flagPole')}
              style={{
                top: `${y}px`,
                left: `${x}px`,
                height: `${height}px`,
                background: color,
              }}
              data-testid="flag-pole"
            />}
          </Fragment>,
        )}
      </div>
    );
  }
}

function getSelectionLines(
  surface: IBounds,
  nodes: Map<KeyPath, INodePoint>,
  selections: Iterable<Selection>,
): IColorRect[] {
  const lines: IColorRect[] = [];

  try {
    for (const selection of selections) {
      const { color } = selection;
      const lineRects = getSelectionRects(selection, nodes);

      if (lineRects.length < 1) continue;

      for (const { left, top, width, height } of lineRects) {
        lines.push({
          colors: [color],
          top:    top - surface.top,
          left:   left - surface.left,
          width,
          height,
        });
      }
    }
  } catch (error) {
    // This is to catch potential DOM implementation inconsistencies
    // and one-off errors when dealing with `Range`s
    notify(error, { name: 'getSelectionLines' });
  }

  return splitOverlappingRects(lines);
}

function getSelectionRects(
  selection: Selection,
  nodes: Map<KeyPath, INodePoint>,
): readonly IRect[] {
  const rects: Array<Iterable<DOMRect | ClientRect>> = [];
  const { paths } = selection;

  for (const path of paths) {
    const nodePoint = nodes.get(path);
    if (!nodePoint) continue;

    if (nodePoint.block) {
      rects.push(nodePoint.node.getClientRects());
    } else {
      const startOffset = path === paths.first() ? selection.startOffset : 0;
      const endOffset = path === paths.last() ? selection.endOffset : -1;

      const start = findNodeAtOffset(nodePoint.node, startOffset);
      const end = findNodeAtOffset(nodePoint.node, endOffset);

      if (start && end) {
        // Use range to find client rects of the selection range
        const range = document.createRange();
        range.setStart(start.node, start.offset);
        range.setEnd(end.node, end.offset);
        rects.push(range.getClientRects());
      }
    }
  }
  return mergeLines(flattenIterators(rects));
}

function getSelectionFlags(
  /**
   * Bounds for all the contents that sit under this
   * SharedSelectionRender instance
   */
  surface: IBounds,
  /**
   * Bounds and scroll position for the scrolling viewport
   */
  scrollViewport: IScrollEvent | undefined,
  /**
   * TODO Having to include this is kind of unfortunate, perhaps
   * we can integrate various properties of viewports, like size,
   * scroll position, having fixed elements on each side into one
   * component at some point.
   */
  stickyContainerHeight: number,
  nodes: Map<KeyPath, INodePoint>,
  selections: Iterable<Selection>,
  getLabel: IPresenceContext['getParticipantName'],
): IFlag[] {
  const flags: IFlag[] = [];

  try {
    for (const selection of selections) {
      const flag = getFlag(
        selection,
        surface,
        scrollViewport,
        stickyContainerHeight,
        nodes,
        getLabel,
      );

      if (flag) {
        flags.push(flag);
      }
    }
  } catch (error) {
    // This is to catch potential DOM implementation inconsistencies
    // and one-off errors when dealing with `Range`s
    notify(error, { name: 'getSelectionFlags' });
  }

  return flags;
}

function getFlag(
  { cursorPosition, color, user, paths, startOffset, endOffset }: Selection,
  surface: IBounds,
  scrollViewport: IScrollEvent | undefined,
  stickyContainerHeight: number,
  nodes: Map<KeyPath, INodePoint>,
  getLabel: IPresenceContext['getParticipantName'],
): IFlag | undefined {
  let flagPosition: IFlagPosition | undefined;

  if (cursorPosition === CursorPosition.Start) {
    for (let i = 0; !flagPosition && i < paths.size; ++i) {
      const nodePoint = nodes.get(paths.get(i)!);
      if (nodePoint) {
        flagPosition = findFlagPosition(
          nodePoint,
          i === 0 ? startOffset : 0,
          cursorPosition,
          surface,
          scrollViewport,
          stickyContainerHeight,
        );
      }
    }
  } else {
    for (let i = paths.size - 1; !flagPosition && i >= 0; --i) {
      const nodePoint = nodes.get(paths.get(i)!);

      if (nodePoint) {
        flagPosition = findFlagPosition(
          nodePoint,
          i === (paths.size - 1) ? endOffset : -1,
          cursorPosition,
          surface,
          scrollViewport,
          stickyContainerHeight,
        );
      }
    }
  }

  if (!flagPosition) return;

  return {
    ...flagPosition,
    // Prevent the label from displaying off-screen. This is a
    // little crude method and won't work for labels longer than
    // 150px.
    //
    // TODO: We probably want a little more sophisticated method
    position: flagPosition.x < 150 ? CursorPosition.End : CursorPosition.Start,
    // We can asssume that peer exists (the `!`) because we
    // filter the non-existing ones when creating selections
    // var
    label: getLabel(user),
    color,
    textColor: getFlagLabelTextColor(color),
  };
}

function findFlagPosition(
  nodePoint: INodePoint,
  nodeOffset: number,
  cursorPosition: CursorPosition,
  surface: IBounds,
  scrollViewport: IScrollEvent | undefined,
  stickyContainerHeight: number,
): IFlagPosition | undefined {
  let rect: DOMRect | ClientRect | undefined;
  let drawOnRightEdge = false;
  let isNewlineRange = false;
  let isOnlySpace = false;

  if (nodePoint.block) {
    rect = nodePoint.node.getBoundingClientRect();
    drawOnRightEdge = cursorPosition === CursorPosition.End;
  } else {
    const range = document.createRange();

    const selectedNode = findNodeAtOffset(
      nodePoint.node,
      nodeOffset,
    );

    if (!selectedNode) return;

    const { node, offset } = selectedNode;
    const length = textLength(node);

    // Initial and following whitespace (that's not rendered) will
    // give a zero-size selection in wrong place in WebKit browsers
    // so we need the range not include that whitespace.
    const leftPad = leftWhiteSpaceLength(node);
    const rightPad = rightWhiteSpaceLength(node);

    // If there are spaces on the left or right sides, `leftPad`
    // is the actual start of the string and `length - leftPad -
    // rightPad` is the actual length of what's rendered. (spaces
    // on both ends are not rendered).
    if (length === leftPad) {
      // when node contains spaces only
      isOnlySpace = true;
      range.setStart(node, offset);
      range.setEnd(node, offset);
    } else if (offset <= leftPad) {
      // Create a range on the start of the node
      range.setStart(node, leftPad);
      range.setEnd(node, leftPad + 1);
    } else if (offset >= length - rightPad) {
      drawOnRightEdge = true;
      range.setStart(node, length - rightPad - 1);
      range.setEnd(node, length - rightPad);
    } else {
      // Create a range in the middle of the node the flagpole will
      // have to be on the right edge of the
      drawOnRightEdge = true;
      range.setStart(node, offset - 1);
      range.setEnd(node, offset);
    }

    isNewlineRange = range.toString() === '\n';
    rect = range.getBoundingClientRect();
  }

  // If last character of the selection is a newline character
  // or the node contains spaces only
  // then the range rectangle width will be 0.
  // Still, we want to draw the flag
  if (!rect || (!isNewlineRange && !isOnlySpace && rect.width === 0) || rect.height === 0) return undefined;

  const x = (drawOnRightEdge ? rect.right : rect.left);
  const y = rect.top;

  const targetScrollTop =
    scrollViewport
    ? (
      // Adjust present scroll position
      scrollViewport.position.y
      // with position of the flag relative to the viewport.
      + y - scrollViewport.bounds.top
      // Make it scroll to around the middle of the viewport.
      - (scrollViewport.bounds.height / 2)
    )
    : 0;

  const isScrollViewportScrollable = scrollViewport &&
                                     scrollViewport.bounds.height !== 0 &&
                                     scrollViewport.bounds.width !== 0;

  // Make the flag show fixed on top or bottom of the viewport if
  // it's not currently visible
  if (scrollViewport && isScrollViewportScrollable && y < scrollViewport.bounds.top + stickyContainerHeight) {
    // The flag is above top edge of the viewport (because its
    // scrolled past the position where we expect it to be) or it's
    // covered by sticky container
    return {
      x,
      y: scrollViewport.bounds.top + stickyContainerHeight,
      fixed: FixedType.Top,
      targetScrollTop,
      height: 0,
    };
  } else if (scrollViewport && isScrollViewportScrollable && y > scrollViewport.bounds.bottom) {
    // The flag is below the bottom edge of the viewport
    return {
      x,
      y: scrollViewport.bounds.top + scrollViewport.bounds.height,
      fixed: FixedType.Bottom,
      targetScrollTop,
      height: 0,
    };
  } else {
    return {
      x: x - surface.left,
      y: y - surface.top,
      height: rect.height,
    };
  }
}

function getBackground(colors: readonly string[]): string {
  if (colors.length === 0) return '';
  if (colors.length === 1) return colors[0];

  const regions = colors.map((color, i) => {
    return `${color} ${i / colors.length * 100}%, ${color} ${(i + 1) / colors.length * 100}%`;
  });

  return `linear-gradient(to bottom, ${regions.join(', ')})`;
}

const labelStyles: IStyles = {
  fontSize:   '12px',
  lineHeight: '16px',
  fontWeight: 600,
  padding:    '2px 5px',
  whiteSpace: 'nowrap',
};

const fadeOutAnimation = keyframes({
  from: { opacity: 1 },
  to: { opacity: 0 },
});

const Styles = createStyles({
  wrap: {
    position: 'relative',
  },
  block: {
    position: 'absolute',
    pointerEvents: 'none',
    opacity: 0.4,
    // This blend mode will allow back text underneath to stay black
    mixBlendMode: 'multiply',
  },
  flagPole: {
    position: 'absolute',
    pointerEvents: 'none',
    width: `${FLAG_POLE_WIDTH}px`,
    mixBlendMode: 'multiply',
    transform: `translateX(${-FLAG_POLE_WIDTH / 2}px)`,
  },
  flagLabel: ({ position, forceDisplay }) => ({
    ...labelStyles,
    position:      'absolute',
    pointerEvents: 'none',
    transform:
      position === CursorPosition.Start
      ? `translateX(-100%) translateX(${FLAG_POLE_WIDTH / 2}px) translateY(-100%)`
      : `translateX(${-FLAG_POLE_WIDTH / 2}px) translateY(-100%)`,
    animation: forceDisplay
              ? 'none'
              : `${fadeOutAnimation}
                 ${SELECTION_FLAG_FADEOUT_DURATION_SECONDS}s ease
                 ${SELECTION_FLAG_FADEOUT_DELAY_SECONDS}s forwards`,
  }),
  fixedFlag: ({ fixed, position }) => ({
    position: 'fixed',
    transition: 'left 0.2s, transform 0.2s',
    transform: [
      fixed === FixedType.Top ? 'translateY(8px)' : 'translateY(-100%) translateY(-8px)',
      position === CursorPosition.Start ? 'translateX(-100%)' : '',
    ].join(' '),
    zIndex: 1,
  }),
  fixedFlagArrow: ({ fixed }) => [
    {
      position: 'absolute',
      width: '10px',
      height: '10px',
      left: '50%',
      transformOrigin: '50% 50%',
      transform: [
        fixed === FixedType.Top ? 'translateY(50%)' : 'translateY(-50%)',
        'translateX(-50%) rotate(45deg)',
      ].join(' '),
    },
    fixed === FixedType.Top && {
      bottom: '100%',
    },
    fixed === FixedType.Bottom && {
      top: '100%',
    },
  ],
  fixedFlagText: {
    ...labelStyles,
    position: 'relative',
    cursor: 'pointer',
  },
  resetStackingContext: {
    position: 'relative',
    zIndex: 0,
  },
});

const SharedSelectionRenderInjectDependencies: FunctionComponent<IProps> = ({ ...props }) => {
  const peers = usePeers();
  const stickyContainerHeight = useStickyHeight();
  const [size, measureRef] = useMeasure();
  const { entityKeyPathState } = useEntityStateSyncContext();
  const { getParticipantName } = usePresenceContext();

  if (!peers) return <>{props.children}</>;

  return (
    <Styles>{(css) =>
      <ScrollCtrlConsumer>{({ scrollTo }) =>
        <GetScroll throttle={SCROLL_THROTTLE}>{(scrollViewport) =>
          <SharedSelectionRender
            {...props}
            scrollViewport={scrollViewport}
            scrollTo={scrollTo}
            size={size}
            stickyContainerHeight={stickyContainerHeight}
            css={css}
            getParticipantName={getParticipantName}
            keyPathRootState={entityKeyPathState}
            measureRef={measureRef}
          />
        }</GetScroll>
      }</ScrollCtrlConsumer>
    }</Styles>
  );
};

export default SharedSelectionRenderInjectDependencies;
